import {
  getExcelAchievementsColumnOptions,
  getExcelCalendarEventsColumnOptions,
  getExcelCompletionHistoryColumnOptions,
  getExcelCoursesColumnOptions,
  getExcelLearningPathsColumnOptions,
  getExcelUserColumnOptions
} from '.';

import type {
  LocaleMessages,
  Locales,
  RequestUser,
  ServerConfig
} from 'shared-types';

export const supportedExcelExportTypes = [
  'achievements',
  'calendar-events',
  'completion-history',
  'courses',
  'paths',
  'users'
] as const;

export type SupportedExcelExportTypes =
  (typeof supportedExcelExportTypes)[number];

type Params = {
  language?: Locales;
  messages: LocaleMessages;
  serverConfig: ServerConfig<'admin'>;
  type: SupportedExcelExportTypes;
} & (
  | {
      excludeUserCustomAttributes?: never;
      requestUser: RequestUser;
    }
  | {
      excludeUserCustomAttributes: true;
      requestUser?: never;
    }
);

const getColumnOptionsByProductType = ({
  excludeUserCustomAttributes,
  language,
  messages,
  requestUser,
  serverConfig,
  type
}: Params) => {
  const mapOption = (excelOptions: { [key: string]: string }) =>
    Object.keys(excelOptions).map((key) => ({
      label: excelOptions[key],
      value: key
    }));

  const getterByType = {
    achievements: getExcelAchievementsColumnOptions,
    'calendar-events': getExcelCalendarEventsColumnOptions,
    'completion-history': getExcelCompletionHistoryColumnOptions,
    courses: getExcelCoursesColumnOptions,
    paths: getExcelLearningPathsColumnOptions,
    users: getExcelUserColumnOptions
  };

  const lang = language || serverConfig.defaultSystemLanguage;

  const conditionalParams = excludeUserCustomAttributes
    ? { excludeUserCustomAttributes }
    : { requestUser };

  if (type === 'users') {
    return mapOption(
      getterByType['users']({
        language: lang,
        messages,
        serverConfig,
        ...conditionalParams
      })
    );
  }

  return [
    ...mapOption(
      getterByType['users']({
        language: lang,
        messages,
        serverConfig,
        ...conditionalParams
      })
    ),
    ...mapOption(
      getterByType[type]({
        language: lang,
        messages,
        serverConfig
      })
    )
  ];
};

export default getColumnOptionsByProductType;
